import React, { useMemo } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";

import {
  ColumnDefinition,
  EditingCell,
  EmployeeData,
  TableFilter,
} from "../../EmployeeDataDrawer";
import { SALARY_GROUPS } from "../../helpers";
import { formatDateOrNumericValue } from "../../../../hooks/useEmployeeDataMapper";

const DATE_DISPLAY_COLUMNS = new Set(["age", "yearsOfService"]);

interface EmployeeTableProps {
  columns: ColumnDefinition[];
  paginatedData: EmployeeData[];
  filters: TableFilter[];
  editingCell: EditingCell | null;
  editValue: string;
  setEditValue: React.Dispatch<React.SetStateAction<string>>;
  selectedKeys: Set<string>;
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
  expandedGroups: Set<string>;
  onToggleGroup: (groupId: string) => void;
  onCellClick: (
    rowIndex: number,
    colKey: string,
    value: string | number | undefined,
    rowKey: string,
  ) => void;
  onSaveCell: () => void;
  onKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onToggleRowSelection: (rowKey: string, isSelected: boolean) => void;
  onToggleSelectAllVisible: (checked: boolean) => void;
  onOpenColumnFilterMenu: (columnKey: string, anchorEl: HTMLElement) => void;
  onDeleteEmployee: (rowKey: string) => void;
}

const GROUP_COLORS: Record<string, string> = {
  basePay: "#e3f2fd",
  allowances: "#f3e5f5",
  personalBonuses: "#e8f5e9",
  specialBonuses: "#fff3e0",
};

export const EmployeeTable: React.FC<EmployeeTableProps> = ({
  columns,
  paginatedData,
  filters,
  editingCell,
  editValue,
  setEditValue,
  selectedKeys,
  allVisibleSelected,
  someVisibleSelected,
  expandedGroups,
  onToggleGroup,
  onCellClick,
  onSaveCell,
  onKeyPress,
  onToggleRowSelection,
  onToggleSelectAllVisible,
  onOpenColumnFilterMenu,
  onDeleteEmployee,
}) => {
  const { t } = useTranslation();

  const visibleColumns = useMemo(() => {
    return columns.filter((col) => {
      if (!col.group) return true;
      if (col.isGroupGeneral) return true;

return expandedGroups.has(col.group);
    });
  }, [columns, expandedGroups]);

  const groupHeaderSegments = useMemo(() => {
    const segments: Array<{
      type: "plain" | "group";
      colSpan: number;
      groupId?: string;
      label?: string;
      isExpanded?: boolean;
      bgColor?: string;
    }> = [];

    let i = 0;

    while (i < visibleColumns.length) {
      const col = visibleColumns[i];

      if (!col.group) {
        const lastSeg = segments[segments.length - 1];

        if (lastSeg && lastSeg.type === "plain") {
          lastSeg.colSpan += 1;
        } else {
          segments.push({ type: "plain", colSpan: 1 });
        }
        i++;
      } else {
        const groupId = col.group;
        const groupDef = SALARY_GROUPS.find((g) => g.id === groupId);
        let count = 0;

        while (
          i + count < visibleColumns.length &&
          visibleColumns[i + count].group === groupId
        ) {
          count++;
        }
        segments.push({
          type: "group",
          colSpan: count,
          groupId,
          label: groupDef ? t(groupDef.labelKey, groupDef.defaultLabel) : groupId,
          isExpanded: expandedGroups.has(groupId),
          bgColor: GROUP_COLORS[groupId] || "#f5f5f5",
        });
        i += count;
      }
    }

return segments;
  }, [visibleColumns, expandedGroups, t]);

  return (
    <Box sx={{ flex: 1, overflow: "auto" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          {/* Group header row */}
          <TableRow>
            <TableCell
              padding="checkbox"
              sx={{ backgroundColor: "#f5f5f5", borderBottom: "none" }}
            />
            {groupHeaderSegments.map((seg, idx) =>
              seg.type === "plain" ? (
                <TableCell
                  key={`plain-${idx}`}
                  colSpan={seg.colSpan}
                  sx={{ backgroundColor: "#f5f5f5", borderBottom: "none" }}
                />
              ) : (
                <TableCell
                  key={`group-${seg.groupId}`}
                  colSpan={seg.colSpan}
                  sx={{
                    backgroundColor: seg.bgColor,
                    fontWeight: "bold",
                    fontSize: "11px",
                    textAlign: "center",
                    borderBottom: "none",
                    py: 0.5,
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={0.5}
                  >
                    <span>{seg.label}</span>
                    <Tooltip
                      title={
                        seg.isExpanded
                          ? t("paygap.employeeData.group.collapse", "Collapse")
                          : t("paygap.employeeData.group.expand", "Expand")
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => seg.groupId && onToggleGroup(seg.groupId)}
                        sx={{ p: 0.25 }}
                      >
                        <FeatherIcon
                          icon={seg.isExpanded ? "minus-circle" : "plus-circle"}
                          size={14}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              ),
            )}
            <TableCell
              sx={{ backgroundColor: "#f5f5f5", borderBottom: "none" }}
            />
          </TableRow>

          {/* Column header row */}
          <TableRow>
            <TableCell
              padding="checkbox"
              sx={{ backgroundColor: "#f5f5f5", width: "48px" }}
            >
              <Checkbox
                size="small"
                indeterminate={someVisibleSelected}
                checked={allVisibleSelected}
                onChange={(event): void =>
                  onToggleSelectAllVisible(event.target.checked)
                }
              />
            </TableCell>
            {visibleColumns.map((col) => {
              const hasFilter = filters.some(
                (filter) => filter.columnKey === col.key,
              );
              const bgColor = col.group
                ? GROUP_COLORS[col.group] || "#f5f5f5"
                : "#f5f5f5";

              return (
                <TableCell
                  key={col.key}
                  sx={{ fontWeight: "bold", backgroundColor: bgColor }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                  >
                    <span>{col.label}</span>
                    <IconButton
                      size="small"
                      onClick={(event): void =>
                        onOpenColumnFilterMenu(col.key, event.currentTarget)
                      }
                      color={hasFilter ? "primary" : "default"}
                    >
                      <FeatherIcon icon="filter" size={14} />
                    </IconButton>
                  </Box>
                </TableCell>
              );
            })}
            <TableCell
              sx={{
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                width: "60px",
              }}
            >
              {t("paygap.employeeData.column.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedData.map((row, index) => (
            <TableRow key={row.key || index} hover>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={selectedKeys.has(String(row.key))}
                  onChange={(event): void =>
                    onToggleRowSelection(String(row.key), event.target.checked)
                  }
                />
              </TableCell>
              {visibleColumns.map((col) => {
                const isEditing =
                  editingCell?.rowIndex === index && editingCell?.colKey === col.key;
                const value = row[col.key as keyof typeof row];

                return (
                  <TableCell
                    key={col.key}
                    sx={{
                      fontSize: "12px",
                      cursor: "pointer",
                      backgroundColor: isEditing ? "#f0f8ff" : "transparent",
                      "&:hover": {
                        backgroundColor: isEditing ? "#f0f8ff" : "#f5f5f5",
                      },
                    }}
                    onClick={() =>
                      !isEditing &&
                      onCellClick(index, col.key, value, String(row.key))
                    }
                  >
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={editValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditValue(e.target.value)
                        }
                        onKeyDown={onKeyPress}
                        onBlur={onSaveCell}
                        autoFocus
                        sx={{
                          width: "100%",
                          "& .MuiInputBase-root": {
                            fontSize: "12px",
                            height: "24px",
                            minHeight: "24px",
                            padding: "0 2px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "0 2px",
                            height: "24px",
                            lineHeight: "24px",
                            fontSize: "12px",
                          },
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          height: "24px",
                          fontSize: "12px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "100%",
                        }}
                      >
                        {col.key === "gender"
                          ? value === 0
                            ? t("paygap.data.gender.male", "Male")
                            : value === 1
                              ? t("paygap.data.gender.female", "Female")
                              : value || "-"
                          : DATE_DISPLAY_COLUMNS.has(col.key)
                            ? formatDateOrNumericValue(value)
                            : value || "-"}
                      </span>
                    )}
                  </TableCell>
                );
              })}
              <TableCell sx={{ width: "60px", textAlign: "center" }}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDeleteEmployee(String(row.key))}
                  sx={{
                    "&:hover": {
                      backgroundColor: "rgba(211, 47, 47, 0.08)",
                    },
                  }}
                >
                  <FeatherIcon icon="trash-2" size={16} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
